import React, { useEffect, useState } from "react";
import Reveal from "./Reveal";
import { ChevronDown } from "lucide-react";

/**
 * Authentic Momentum FAQ.
 *
 * Renders the AEO question set as accessible disclosure rows AND
 * publishes the matching `FAQPage` JSON-LD payload so answer
 * engines (Google AI Overviews, Perplexity, ChatGPT browsing) can
 * extract structured answers to:
 *  - "What is Authentic Momentum?"
 *  - "Who is Authentic Momentum for?"
 *  - "How does Cyrus help?"
 *  - "What is the first step?"
 *  - "What is the Structured Momentum Method?"
 *
 * Drop this onto any page where the FAQ should appear. The JSON-LD
 * tag is removed on unmount so other pages do not inherit the
 * schema.
 */

const FAQS = [
  {
    q: "What is Authentic Momentum?",
    a: "Authentic Momentum is a clarity and momentum framework that helps people turn scattered potential into structured movement. It supports founders, creators, visionaries, and transition-stage professionals in clarifying what they are building, identifying what is blocking progress, and creating practical next steps.",
  },
  {
    q: "Who is Authentic Momentum for?",
    a: "Authentic Momentum is for people who feel they are carrying ideas, gifts, opportunities, or vision that need clearer language, structure, and execution. It is especially useful for founders, creators, consultants, healers, operators, and professionals in transition.",
  },
  {
    q: "How does Cyrus help?",
    a: "Cyrus helps people listen deeply to what is present, identify the real bottleneck, clarify the signal, structure the next move, and activate momentum through strategy, systems, AI tools, brand architecture, relationship mapping, and execution planning.",
  },
  {
    q: "What is the Structured Momentum Method?",
    a: "The Structured Momentum Method is the six-step process used inside the work: Listen, Locate, Clarify, Structure, Activate, Prove. It moves a person from inner clarity to outer execution and visible result.",
  },
  {
    q: "What is the first step?",
    a: "The first step is usually the free Clarity Snapshot or a Clarity Reset Session to identify where momentum is blocked and what kind of structure is needed next.",
  },
  {
    q: "How do I work with Cyrus?",
    a: "Begin with a Clarity Reset Session. From there, deeper engagements include a Structured Momentum Sprint for brand or business buildout, and a Strategic Architecture Container for long-term advisory and systems design.",
  },
];

const TAG = "data-am-faq-jsonld";

const AuthenticMomentumFAQ = () => {
  const [openIndex, setOpenIndex] = useState(0);

  useEffect(() => {
    const payload = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    };
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.setAttribute(TAG, "authentic-momentum-faq");
    el.textContent = JSON.stringify(payload);
    document.head.appendChild(el);
    return () => el.remove();
  }, []);

  return (
    <section
      id="authentic-momentum-faq"
      data-testid="authentic-momentum-faq-section"
      className="relative py-24 md:py-32 border-t border-gold/10"
    >
      <div className="max-w-3xl mx-auto px-6 md:px-10">
        <Reveal>
          <p className="text-[11px] tracking-[0.5em] uppercase text-gold mb-6 text-center">
            Quick Answers
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-themed leading-tight tracking-tight font-light text-center">
            Authentic Momentum,{" "}
            <span className="italic gold-gradient-text">in plain language.</span>
          </h2>
        </Reveal>

        <ul className="mt-12 space-y-3" data-testid="faq-list">
          {FAQS.map((f, i) => {
            const open = openIndex === i;
            return (
              <li
                key={i}
                data-testid={`faq-item-${i}`}
                className="bg-white/5 border-t border-gold/20 rounded-lg border border-gold/10 overflow-hidden"
              >
                <button
                  type="button"
                  onClick={() => setOpenIndex(open ? -1 : i)}
                  aria-expanded={open}
                  data-testid={`faq-toggle-${i}`}
                  className="w-full text-left p-6 md:p-7 flex items-center justify-between gap-4 hover:bg-white/[0.02] transition-colors"
                >
                  <span className="font-serif text-[18px] md:text-[19px] text-themed font-light pr-2">
                    {f.q}
                  </span>
                  <ChevronDown
                    size={18}
                    strokeWidth={1.5}
                    className={`shrink-0 text-gold transition-transform duration-300 ${open ? "rotate-180" : ""}`}
                    aria-hidden
                  />
                </button>
                {open ? (
                  <div
                    data-testid={`faq-answer-${i}`}
                    className="px-6 md:px-7 pb-6 md:pb-7 text-white/85 text-[15.5px] leading-relaxed"
                  >
                    {f.a}
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
};

export default AuthenticMomentumFAQ;
